import { BarChart3, Search, SlidersHorizontal, X } from "lucide-react";
import TableFilterDrawer from "./TableFilterDrawer";

export default function TableToolbar({
  config,
  filters,
  setFilter,
  resetFilters,
  removeFilter,
  activeCount,
  activeChips,
  metrics,
  drawerOpen,
  setDrawerOpen,
  fieldOptions,
  totalCount,
  filteredCount,
  className = "",
}) {
  return (
    <>
      <div className={`space-y-3 ${className}`}>
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <label className="relative block w-full lg:max-w-md">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-primary/30" />
            <input
              type="search"
              value={filters.search ?? ""}
              onChange={(e) => setFilter("search", e.target.value)}
              placeholder={config.searchPlaceholder ?? "Search records…"}
              className="w-full rounded-xl border border-primary/10 bg-white py-2.5 pl-9 pr-3 text-sm text-primary placeholder:text-primary/30 shadow-sm focus:border-accent/50 focus:outline-none focus:ring-2 focus:ring-accent/20"
            />
          </label>

          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className={`inline-flex items-center justify-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-semibold transition-colors ${
              activeCount > 0
                ? "border-primary bg-primary text-white shadow-md shadow-primary/20"
                : "border-primary/10 bg-white text-primary/70 shadow-sm hover:border-primary/20 hover:text-primary"
            }`}
          >
            <SlidersHorizontal className="h-4 w-4" />
            Filters
            {activeCount > 0 && (
              <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-white/20 px-1.5 text-[10px] font-bold">
                {activeCount}
              </span>
            )}
          </button>
        </div>

        {metrics?.length > 0 && (
          <div className="flex flex-nowrap gap-2 overflow-x-auto pb-0.5">
            {metrics.map((metric) => (
              <div
                key={metric.key}
                className="relative min-w-[10rem] flex-1 shrink-0 overflow-hidden rounded-xl border border-primary/[0.07] bg-white px-3 py-2.5 shadow-[0_1px_2px_rgba(127,29,29,0.04)]"
              >
                <div className="absolute inset-y-0 left-0 w-[3px] bg-primary/30" aria-hidden />
                <div className="flex items-center gap-2 pl-1">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-primary/[0.06] text-primary/45">
                    <BarChart3 className="h-3 w-3" />
                  </span>
                  <p className="truncate text-[10px] font-semibold uppercase tracking-[0.06em] text-primary/45">
                    {metric.label}
                  </p>
                </div>
                <p className="mt-1 pl-1 text-sm font-bold tabular-nums text-primary sm:text-base">
                  {metric.display}
                </p>
                {metric.sub && (
                  <p className="mt-0.5 truncate pl-1 text-[10px] text-primary/40">{metric.sub}</p>
                )}
              </div>
            ))}
          </div>
        )}

        {(activeChips.length > 0 || activeCount > 0) && (
          <div className="flex flex-wrap items-center gap-2 rounded-xl border border-primary/[0.07] bg-primary/[0.02] px-3 py-2.5">
            <span className="text-[11px] font-semibold text-primary/45">
              {filteredCount.toLocaleString()} of {totalCount.toLocaleString()} shown
            </span>
            {activeChips.map((chip) => (
              <button
                key={chip.key}
                type="button"
                onClick={() => removeFilter(chip.key)}
                className="inline-flex items-center gap-1 rounded-lg bg-white px-2 py-1 text-[11px] font-semibold text-primary/70 ring-1 ring-primary/[0.08] transition-colors hover:text-primary"
              >
                {chip.label}
                <X className="h-3 w-3 text-primary/35" />
              </button>
            ))}
            {activeCount > 1 && (
              <button
                type="button"
                onClick={resetFilters}
                className="text-[11px] font-semibold text-primary/50 underline-offset-2 hover:text-primary hover:underline"
              >
                Clear all
              </button>
            )}
          </div>
        )}
      </div>

      <TableFilterDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        config={config}
        filters={filters}
        setFilter={setFilter}
        resetFilters={resetFilters}
        fieldOptions={fieldOptions}
        activeCount={activeCount}
        filteredCount={filteredCount}
        totalCount={totalCount}
      />
    </>
  );
}
